import { useState, useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import SettingsCard from "./ui/SettingsCard";
import SettingsRow from "./ui/Settingrow";
import s from "./section.module.css";
import {
  useEditProfileMutation,
  useGetProfileQuery,
} from "../../store/api/profileApi";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import ImagePreview from "../common/ImagePreview";

const initialState = {
  name: "",
  userType: "",        // ✅ added
  rookieType: "",
  email: "",
  contactNumber: "",
  gender: "",
  dateOfBirth: "",
};

export default function AccountSection() {
  const navigate = useNavigate();
  const { logout } = useAuth();

  const { data: user, isLoading, isError } = useGetProfileQuery();
  const [editProfile, { isLoading: isSaving }] = useEditProfileMutation();

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(initialState);
  const [draft, setDraft] = useState(initialState);

  // 🔹 Map API user → local state
  const mappedUser = useMemo(() => {
    if (!user) return initialState;

    return {
      name: user?.name || "",
      userType: user?.userType || "",     // ✅ added
      rookieType: user?.rookieType || "",
      email: user?.email || "",
      contactNumber: user?.contactNumber || "",
      gender: user?.gender || "",
      dateOfBirth: user?.dateOfBirth
        ? user.dateOfBirth.split("T")[0]
        : ""
    };
  }, [user]);

  useEffect(() => {
    setForm(mappedUser);
    setDraft(mappedUser);
  }, [mappedUser]);

  // ✅ improved change handler
  const handleChange = (field) => (e) => {
    const value = e.target.value;

    setDraft((prev) => ({
      ...prev,
      [field]: value,
      ...(field === "userType" && value !== "Rookie"
        ? { rookieType: "" }
        : {}),
    }));
  };

  const handleSave = async () => {
    try {
      const { name, gender, userType, rookieType, contactNumber } = draft;

      if (!name || !gender || !userType || !contactNumber) {
        toast.error("Please fill all required fields");
        return;
      }

      const formData = new FormData();

      formData.append("Name", name);
      formData.append("Gender", gender);
      formData.append("UserType", userType);       // ✅ fixed
      formData.append("ContactNumber", contactNumber);

      if (userType === "Rookie" && rookieType) {
        formData.append("RookieType", rookieType); // ✅ correct
      }

      if (draft.email) formData.append("Email", draft.email);
      if (draft.dateOfBirth) {
        formData.append("DateOfBirth", draft.dateOfBirth);
      }

      await editProfile(formData).unwrap();

      toast.success("Profile updated successfully!");
      setForm(draft);
      setEditing(false);
    } catch (error) {
      const messages =
        error?.data &&
        Object.values(error.data).flat().join("\n");

      toast.error(messages || "Update failed");
    }
  };

  const handleCancel = () => {
    setDraft(form);
    setEditing(false);
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  // 🔹 Format DOB nicely
  const formattedDOB = useMemo(() => {
    if (!form.dateOfBirth) return "N/A";
    return new Date(form.dateOfBirth).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  }, [form.dateOfBirth]);

  if (isLoading) return <p>Loading account details...</p>;
  if (isError || !user) return <p>Failed to load account details.</p>;

  return (
    <>
      <SettingsCard
        title="Profile Information"
        subtitle="Your personal and contact details."
      >
        {/* Avatar */}
        <div className={s.avatarRow}>
          <div className={s.avatar}>
            {user?.profilePhotoUrl ? (
              <ImagePreview
                src={user.profilePhotoUrl}
                alt="Profile"
                className={s.avatarImage}
              />
            ) : (
              <span className={s.avatarInitial}>
                {form.name?.charAt(0)?.toUpperCase() || "U"}
              </span>
            )}
          </div>

          <div className={s.avatarInfo}>
            <div className={s.avatarName}>{form.name}</div>

            {/* ✅ One-line UserType + (RookieType) */}
            <div className={s.avatarSub}>
              {form.userType
                ? form.userType === "Rookie" && form.rookieType
                  ? `${form.userType} (${form.rookieType})`
                  : form.userType
                : "N/A"}
            </div>
          </div>

          {!editing && (
            <button
              className={s.btnOutline}
              onClick={() => setEditing(true)}
            >
              Edit Profile
            </button>
          )}
        </div>

        <div className={s.padded}>
          {editing ? (
            <>
              {/* Row 1 */}
              <div className={s.fieldRow}>
                <div className={s.field}>
                  <label className={s.label}>Full Name</label>
                  <input
                    className={s.input}
                    value={draft.name}
                    onChange={handleChange("name")}
                  />
                </div>

                <div className={s.field}>
                  <label className={s.label}>User Type</label>
                  <select
                    className={s.input}
                    value={draft.userType}
                    onChange={handleChange("userType")}
                  >
                    <option value="">Select</option>
                    <option value="Rookie">Rookie</option>
                    <option value="Entrepreneur">Entrepreneur</option>
                    <option value="Business Professional">Business Professional</option>
                    <option value="Investor">Investor</option>
                    <option value="Mentor">Mentor</option>
                    <option value="Others">Others</option>
                  </select>
                </div>
              </div>

              {/* RookieType (conditional) */}
              {draft.userType === "Rookie" && (
                <div className={s.field}>
                  <label className={s.label}>Rookie Type</label>
                  <select
                    className={s.input}
                    value={draft.rookieType}
                    onChange={handleChange("rookieType")}
                  >
                    <option value="">Select one</option>
                    <option value="Student">Student</option>
                    <option value="Working Professional">Working Professional</option>
                    <option value="Others">Others</option>
                  </select>
                </div>
              )}

              {/* Row 2 */}
              <div className={s.fieldRow}>
                <div className={s.field}>
                  <label className={s.label}>Email</label>
                  <input
                    type="email"
                    className={s.input}
                    value={draft.email}
                    onChange={handleChange("email")}
                  />
                </div>

                <div className={s.field}>
                  <label className={s.label}>Phone</label>
                  <input
                    className={s.input}
                    value={draft.contactNumber}
                    onChange={handleChange("contactNumber")}
                  />
                </div>
              </div>

              {/* Row 3 */}
              <div className={s.fieldRow}>
                <div className={s.field}>
                  <label className={s.label}>Gender</label>
                  <select
                    className={s.select}
                    value={draft.gender}
                    onChange={handleChange("gender")}
                  >
                    <option value="">Select</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div className={s.field}>
                  <label className={s.label}>Date of Birth</label>
                  <input
                    type="date"
                    className={s.input}
                    value={draft.dateOfBirth}
                    onChange={handleChange("dateOfBirth")}
                  />
                </div>
              </div>

              {/* Buttons */}
              <div className={s.btnRow}>
                <button
                  className={s.btnPrimary}
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  {isSaving ? "Saving..." : "Save Changes"}
                </button>

                <button
                  className={s.btnOutline}
                  onClick={handleCancel}
                >
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <>
              <div className={s.fieldRow}>
                <div className={s.field}>
                  <div className={s.valueKey}>Email</div>
                  <div className={s.valueText}>
                    {form.email || "N/A"}
                  </div>
                </div>

                <div className={s.field}>
                  <div className={s.valueKey}>Phone</div>
                  <div className={s.valueText}>
                    {form.contactNumber || "N/A"}
                  </div>
                </div>
              </div>

              <div className={s.fieldRow}>
                <div className={s.field}>
                  <div className={s.valueKey}>Gender</div>
                  <div className={s.valueText}>
                    {form.gender || "N/A"}
                  </div>
                </div>

                <div className={s.field}>
                  <div className={s.valueKey}>Date of Birth</div>
                  <div className={s.valueText}>
                    {formattedDOB || "N/A"}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </SettingsCard>

      <SettingsCard title="Account Management">
        <SettingsRow
          label="Logout"
          sublabel="Sign out of your account."
          onClick={handleLogout}
        />
      </SettingsCard>
    </>
  );
}